(function () { 'use strict';
// Display only: never changes consent, pixels, cart, account, prices or checkout.
// Daily frequency stores only a local calendar date. No click ID, referrer,
// attribution profile or other visitor data is persisted or transmitted.
const DAILY_KEY = 'cc-campaign-external-daily:v1';
const LOCK_KEY = 'cc-campaign-external-daily';
const INTERNAL_SOURCES = new Set(['internal', 'onsite', 'on-site', 'website', 'site', 'direct', '(direct)', 'homepage', 'navigation']);
const CLICK_KEYS = ['fbclid', 'gclid', 'dclid', 'msclkid', 'ttclid', 'gbraid', 'wbraid'];
function externalArrival(win, doc) {
  try {
    const url = new win.URL(win.location.href);
    const ownHost = url.hostname.toLowerCase().replace(/^www\./, '');
    const internalHost = host => host.toLowerCase() === ownHost || host.toLowerCase().endsWith('.' + ownHost);
    if (doc.referrer) {
      const referrer = new win.URL(doc.referrer);
      if (/^(https?:)$/.test(referrer.protocol)) return !internalHost(referrer.hostname);
    }
    // Referrer-less ad/email/app entries can still carry explicit source tags.
    // Do not infer outside traffic from an unmarked direct visit.
    if (CLICK_KEYS.some(key => url.searchParams.getAll(key).some(value => value.trim().length > 0))) return true;
    return url.searchParams.getAll('utm_source').some(value => {
      const source = value.trim().toLowerCase();
      return source.length > 0 && !INTERNAL_SOURCES.has(source) && !internalHost(source);
    });
  } catch (_) { /* Unavailable entry data cannot distinguish external/direct. */ }
  return false;
}
function localDay(win) {
  const now = new win.Date();
  return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
}
class StoreCampaignPopup {
  constructor(dialog, win = window) {
    this.dialog = dialog; this.win = win; this.doc = dialog.ownerDocument;
    this.shown = false; this.canceled = false; this.previousFocus = null;
    this.page = dialog.dataset.ccCampaignPage;
    this.external = (this.page === 'product' || this.page === 'collection') && externalArrival(win, this.doc);
    this.mode = this.page === 'index' ? 'home-refresh' : this.external ? 'external-daily' : 'manual-only';
    dialog.dataset.ccCampaignMode = this.mode;
    this.closeButton = dialog.querySelector('[data-cc-campaign-close]');
    this.closeButton?.addEventListener('click', () => this.close());
    dialog.querySelector('[data-cc-campaign-continue]')?.addEventListener('click', () => this.close());
    dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
    dialog.addEventListener('close', () => {
      if (dialog.open) { this.interruptedClose = false; return; }
      if (this.interruptedClose) this.interruptedClose = false;
      else this.canceled = true;
      this.unlock();
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    });
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('button,a[href]')].filter(node => !node.hidden && !node.disabled);
      if (event.shiftKey && this.doc.activeElement === controls[0]) { event.preventDefault(); controls.at(-1)?.focus(); }
      else if (!event.shiftKey && this.doc.activeElement === controls.at(-1)) { event.preventDefault(); controls[0]?.focus(); }
    });
    this.doc.addEventListener('click', event => {
      const path = event.composedPath?.() || [event.target];
      const opener = path.find(node => node instanceof win.Element && node.matches('[data-cc-campaign-open]'));
      if (opener) { event.preventDefault(); this.open(true); return; }
      if (path.some(node => node instanceof win.Element && node.matches('shopify-account, [data-cc-account-login]'))) this.suspend('account');
    });
    this.doc.addEventListener('cardc:account-gate-open', () => this.suspend('account'));
    this.doc.addEventListener('close', event => { if (event.target !== dialog) this.queue(150); }, true);
    this.doc.addEventListener('shopify:cart:lines-update', event => {
      if (event.action === 'add') this.suspend('cart');
    }, true);
    // Visible privacy dialogs retain interaction priority; consent values do
    // not gate this informational announcement or get changed by it.
    this.doc.addEventListener('cardclub:privacy-open', () => this.suspend('privacy', false));
    this.doc.addEventListener('visitorConsentCollected', () => this.queue(150));
    this.doc.addEventListener('visibilitychange', () => { if (!this.doc.hidden) this.queue(150); });
    this.observer = new win.MutationObserver(() => {
      if (dialog.open && this.hasOtherDialog()) this.suspend('dialog', false);
      else if (!this.hasOtherDialog()) this.queue(150);
    });
    this.observer.observe(this.doc.body, { attributes: true, attributeFilter: ['open', 'aria-modal'], subtree: true });
    win.addEventListener('pagehide', () => this.suspend('navigation'));
    win.addEventListener('pageshow', event => {
      // Back/forward-cache restoration is not a new homepage refresh.
      if (event.persisted) this.suspend('return');
    });
    for (const opener of this.doc.querySelectorAll('[data-cc-campaign-open]')) {
      opener.hidden = typeof dialog.showModal !== 'function';
      opener.setAttribute('aria-expanded', 'false');
    }
    dialog.dataset.ccCampaignState = this.mode === 'manual-only' ? 'skipped-internal-or-direct' : 'ready';
  }
  readDaily(probe = false) {
    try {
      const storage = this.win.localStorage;
      const raw = storage.getItem(DAILY_KEY);
      const record = raw === null ? {version: 1, day: null} : JSON.parse(raw);
      if (!record || record.version !== 1 || (record.day !== null && (typeof record.day !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(record.day)))) return null;
      const today = localDay(this.win);
      const seen = record.day === today;
      // Test writability without consuming today's allowance. Never overwrite
      // corrupt/future formats or write other stores, cookies or Shopify keys.
      if (probe && !seen) storage.setItem(DAILY_KEY, raw === null ? JSON.stringify(record) : raw);
      return {storage, today, seen};
    } catch (_) { return null; }
  }
  visibleModal(node) {
    if (!node) return false;
    for (let current = node; current; current = current.parentElement || current.getRootNode?.().host) {
      if (current.hidden || current.getAttribute?.('aria-hidden') === 'true') return false;
      const style = this.win.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    }
    return true;
  }
  hasOtherDialog() {
    if (this.visibleModal(this.doc.getElementById('cc-account-gate-notice'))) return true;
    if ([...this.doc.querySelectorAll('dialog[open], [aria-modal="true"]')].some(node => node !== this.dialog && this.visibleModal(node))) return true;
    return [...this.doc.querySelectorAll('shopify-account')].some(host => [...(host.shadowRoot?.querySelectorAll('dialog[open], [aria-modal="true"]') || [])].some(node => this.visibleModal(node)));
  }
  queue(delay = 1000) {
    if (this.shown || this.canceled || this.mode === 'manual-only') return;
    this.win.clearTimeout(this.timer);
    this.timer = this.win.setTimeout(() => {
      const locks = this.win.navigator?.locks;
      if (this.mode === 'external-daily' && typeof locks?.request === 'function') {
        // Serialize the fresh day-check/display/write across tabs where the
        // browser supports Web Locks. Never hold the lock while the user reads.
        try {
          locks.request(LOCK_KEY, {mode: 'exclusive'}, () => this.open()).catch(() => {
            if (!this.shown) this.dialog.dataset.ccCampaignState = 'lock-unavailable';
          });
        } catch (_) { this.dialog.dataset.ccCampaignState = 'lock-unavailable'; }
      } else this.open();
    }, delay);
  }
  open(manual = false) {
    this.win.clearTimeout(this.timer);
    if (this.dialog.open) return true;
    if (!manual && (this.shown || this.canceled || this.mode === 'manual-only')) return false;
    if (this.doc.hidden || this.hasOtherDialog() || typeof this.dialog.showModal !== 'function') return false;
    let daily = null;
    if (this.mode === 'external-daily') {
      daily = this.readDaily(!manual);
      if (!manual && (!daily || daily.seen)) {
        this.dialog.dataset.ccCampaignState = daily ? 'skipped-daily' : 'storage-unavailable';
        return false;
      }
    }
    this.previousFocus = this.doc.activeElement;
    try { this.dialog.showModal(); } catch (_) { return false; }
    this.shown = true;
    if (daily) {
      try { daily.storage.setItem(DAILY_KEY, JSON.stringify({version: 1, day: daily.today})); }
      catch (_) { this.dialog.dataset.ccCampaignPersistence = 'unavailable'; }
    }
    this.dialog.dataset.ccCampaignState = 'open';
    this.doc.documentElement.classList.add('cc-store-campaign-open');
    this.setExpanded(true);
    this.closeButton?.focus({preventScroll: true});
    return true;
  }
  setExpanded(open) {
    for (const opener of this.doc.querySelectorAll('[data-cc-campaign-open]')) opener.setAttribute('aria-expanded', String(open));
  }
  unlock() {
    this.doc.documentElement.classList.remove('cc-store-campaign-open');
    this.setExpanded(false);
    if (!this.hasOtherDialog() && this.previousFocus?.isConnected) this.previousFocus.focus({preventScroll: true});
    this.previousFocus = null;
  }
  close() {
    this.canceled = true; this.win.clearTimeout(this.timer);
    if (this.dialog.open) this.dialog.close();
    this.dialog.dataset.ccCampaignState = 'closed'; this.unlock();
  }
  suspend(reason, cancel = true) {
    if (cancel) this.canceled = true;
    this.win.clearTimeout(this.timer);
    if (this.dialog.open) { this.interruptedClose = true; this.dialog.close(); }
    this.dialog.dataset.ccCampaignState = 'paused-' + reason; this.unlock();
  }
}
function bootStoreCampaign(win = window) {
  const dialog = win.document.getElementById('cc-store-campaign-popup');
  if (!dialog || win.CardcStoreCampaignPopup) return win.CardcStoreCampaignPopup ?? null;
  const controller = new StoreCampaignPopup(dialog, win);
  win.CardcStoreCampaignPopup = controller;
  controller.queue();
  return controller;
}
const start = () => {
  try { bootStoreCampaign(); }
  catch (_) { const node = document.getElementById('cc-store-campaign-popup'); if (node) node.dataset.ccCampaignState = 'startup-error'; }
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true}); else start();
})();
